import React from "react";
import { useNavigate } from "react-router-dom";
import MWrap from "./MWrap";
import MCheckbox from "./MCheckbox";
import styles from "./MDocumentCard.module.css";
import {
  IconPrescription,
  IconFirstAidKit,
  IconClipboardText,
  IconCoinRupee,
  IconShieldHeart,
  IconDotsVertical,
  IconFileSymlink,
  IconFileTextShield,
  IconFileCheck,
  IconUser,
  IconMedicalCross,
  IconChevronRight,
  IconSparkles,
  IconInfoCircle,
  IconStar,
  IconCloudDownload,
  IconShieldLock,
} from "@tabler/icons-react";

import { FileCategory } from "@/types/document.dto";
import MAIModal from "@/components/ui/molecules/MAIModal";
import MDialog from "@/components/ui/atoms/MDialog";
import { useDocument } from "@/hooks/useDocument";
import { getMemberInfo } from "@/hooks/useMembers";

export interface MDocumentCardProps {
  avatar?: React.ReactNode;
  cardType?: "document" | "note";
  onOptionsClick?: () => void;
  selectMode?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  category?: FileCategory | string;
  displayName: React.ReactNode;
  meta?: React.ReactNode;
  onCommentClick?: () => void;
  onDownloadClick?: () => void;
  isPrivate?: boolean;
  availableOffline?: boolean;
  isImportant?: boolean;
  insights?: boolean;
  showComment?: boolean;
  className?: string;
  optionsMenu?: React.ReactNode;
  onClick?: () => void;
  viewer?: boolean;
  onViewDocument?: () => void;
  documentId?: string; // Added for AI insights
  hideActions?: boolean;
  showStatusIcons?: boolean; // New prop to control status icon display
  variant?: "default" | "viewer";
}

const categoryIconMap: Partial<Record<string, JSX.Element>> = {
  [FileCategory.Prescription]: <IconPrescription size={20} />,
  [FileCategory.Other]: <IconFileCheck size={20} />,
  [FileCategory.DischargeSummary]: <IconFirstAidKit size={20} />,
  [FileCategory.LabReport]: <IconClipboardText size={20} />,
  [FileCategory.MedicalBill]: <IconCoinRupee size={20} />,
  [FileCategory.InsuranceCard]: <IconShieldHeart size={20} />,
  [FileCategory.Identity]: <IconUser size={20} />,
  [FileCategory.EmergencyCard]: <IconMedicalCross size={20} />,
  personal: <IconFileSymlink size={20} />,
  family: <IconFileTextShield size={20} />,
};

const categoryLabelMap: Partial<Record<string, string>> = {
  [FileCategory.Prescription]: "Prescription",
  [FileCategory.Other]: "Other",
  [FileCategory.DischargeSummary]: "Procedure",
  [FileCategory.LabReport]: "Report",
  [FileCategory.MedicalBill]: "Bill",
  [FileCategory.InsuranceCard]: "Insurance",
  [FileCategory.Identity]: "Identity",
  [FileCategory.EmergencyCard]: "Emergency",
};

const MDocumentCard: React.FC<MDocumentCardProps> = ({
  avatar: _avatar,
  category,
  displayName,
  meta,
  onCommentClick: _onCommentClick,
  onDownloadClick: _onDownloadClick,
  isPrivate: _isPrivate = false,
  showComment: _showComment = false,
  availableOffline: _availableOffline = false,
  isImportant: _isImportant = false,
  insights = false,
  className = "",
  onOptionsClick,
  optionsMenu,
  onClick: _onClick,
  selectMode = false,
  selected = false,
  onSelect,
  cardType = "note",
  viewer = false,
  onViewDocument,
  documentId,
  hideActions = false,
  showStatusIcons = false,
}) => {
  // Map category to icon, fallback to pill
  const icon = categoryIconMap[category as FileCategory] || (
    <IconPrescription size={20} />
  );
  const categoryLabel =
    categoryLabelMap[category as FileCategory] || "Document";

  // Format date for viewer variant (e.g., "14 Jul 2023")
  const formatViewerDate = (dateString: string) => {
    if (!dateString) return dateString;
    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleString("en-US", { month: "short" });
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch {
      return `Dated ${dateString}`;
    }
  };

  // Navigation hook
  const navigate = useNavigate();

  // AI Modal state
  const [showAIModal, setShowAIModal] = React.useState(false);
  const [showDisabledDialog, setShowDisabledDialog] = React.useState(false);
  const [showNoInsightsDialog, setShowNoInsightsDialog] = React.useState(false);
  const [aiInsights, setAiInsights] = React.useState<any[]>([]);
  const [loadingInsights, setLoadingInsights] = React.useState(false);

  // Monitor showAIModal state changes
  React.useEffect(() => {
    console.log("showAIModal state changed to:", showAIModal);
  }, [showAIModal]);

  // Get member info and document hook
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";
  const { getDocumentInsights } = useDocument(memberId);

  // Check if insights are enabled for the LOGGED-IN member (not selected member)
  // Insights settings belong to the logged-in user, not the profile being viewed
  const loggedInMember = getMemberInfo(true); // ignoreSelectedMember = true
  const insightsEnabled = loggedInMember?.other_attrs?.insights || false;

  // Handle AI preview click
  const handleAIPreviewClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log("AI Preview clicked, insights prop:", insights);
    console.log("AI Preview clicked, documentId:", documentId);

    if (!documentId) {
      console.warn("No documentId provided for AI insights");
      return;
    }

    // Check if insights are disabled
    if (!insightsEnabled) {
      // Show disabled dialog instead of AI modal
      setShowDisabledDialog(true);
      return;
    }

    setLoadingInsights(true);
    try {
      console.log("Fetching insights for document:", documentId);
      const insightsData = await getDocumentInsights(documentId);

      // Check if insights array is empty
      if (
        !insightsData ||
        !Array.isArray(insightsData) ||
        insightsData.length === 0
      ) {
        setShowNoInsightsDialog(true);
        setAiInsights([]);
        return;
      }

      // insightsData is ActionableInsight[] from the hook
      setAiInsights(Array.isArray(insightsData) ? insightsData : []);
      setShowAIModal(true);
    } catch (error) {
      console.error("Error details:", error.response || error.message);
      // Show no insights dialog on error
      setShowNoInsightsDialog(true);
      setAiInsights([]);
    } finally {
      setLoadingInsights(false);
    }
  };

  // Handle view document click
  const handleViewDocumentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onViewDocument) {
      onViewDocument();
    }
  };

  // Helper functions for modal content
  const getModalContent = () => {
    if (loadingInsights) {
      return "Loading AI insights...";
    }
    if (aiInsights && aiInsights.length > 0) {
      // For prescription data, find PRESCRIPTION_SUMMARY type
      const prescriptionSummary = aiInsights.find(
        (item) => item.type === "PRESCRIPTION_SUMMARY"
      );
      if (prescriptionSummary?.summary) {
        // Extract everything before "Next Steps Advised:"
        const summary = prescriptionSummary.summary;
        const parts = summary.split("Next Steps Advised:");
        return parts[0].trim();
      }
      // For lab report data, find LAB_SUMMARY type (but not LAB_SUMMARY_PROCEDURE)
      const labSummary = aiInsights.find((item) => item.type === "LAB_SUMMARY");
      if (labSummary) {
        return labSummary.summary;
      }
      // For LAB_SUMMARY_PROCEDURE, let the MAIModal handle the content
      const labSummaryProcedure = aiInsights.find(
        (item) => item.type === "LAB_SUMMARY_PROCEDURE"
      );
      if (labSummaryProcedure) {
        // Return empty string since MAIModal will handle the procedure-specific content
        return "";
      }
      // For LAB_SUMMARY_IMAGING, let the MAIModal handle the content
      const labSummaryImaging = aiInsights.find(
        (item) => item.type === "LAB_SUMMARY_IMAGING"
      );
      if (labSummaryImaging) {
        // Return empty string since MAIModal will handle the imaging-specific content
        return "";
      }
      // For LAB_SUMMARY_ALLERGY, let the MAIModal handle the content
      const labSummaryAllergy = aiInsights.find(
        (item) => item.type === "LAB_SUMMARY_ALLERGY"
      );
      if (labSummaryAllergy) {
        // Return empty string since MAIModal will handle the allergy-specific content
        return "";
      }
      // For discharge summary data, find DISCHARGE_SUMMARY_INSIGHT type
      const dischargeSummary = aiInsights.find(
        (item) => item.type === "DISCHARGE_SUMMARY_INSIGHT"
      );
      if (dischargeSummary) {
        // Return empty string since DischargeSummaryContent will handle the structured content
        return "";
      }
      // Fallback to first item's summary for other types
      return aiInsights[0]?.summary || "No insights available";
    }
    return "No AI insights available for this document yet.";
  };

  const getWhatsNextContent = () => {
    if (loadingInsights) {
      return "Please wait while we analyze the document...";
    }
    if (aiInsights && aiInsights.length > 0) {
      // For prescription data, extract the "Next Steps Advised" section
      const prescriptionSummary = aiInsights.find(
        (item) => item.type === "PRESCRIPTION_SUMMARY"
      );
      if (prescriptionSummary?.summary) {
        const summary = prescriptionSummary.summary;
        const parts = summary.split("Next Steps Advised:");
        if (parts.length > 1) {
          return parts[1].trim();
        }
      }
      // For LAB_SUMMARY_PROCEDURE, return the next_steps from value
      const labSummaryProcedure = aiInsights.find(
        (item) => item.type === "LAB_SUMMARY_PROCEDURE"
      );
      if (labSummaryProcedure?.value?.next_steps) {
        return labSummaryProcedure.value.next_steps;
      }
      // For LAB_SUMMARY_IMAGING, return the clinical_impression from value
      const labSummaryImaging = aiInsights.find(
        (item) => item.type === "LAB_SUMMARY_IMAGING"
      );
      if (labSummaryImaging?.value?.clinical_impression) {
        return labSummaryImaging.value.clinical_impression;
      }
      // For LAB_SUMMARY_ALLERGY, return the recommendations from value
      const labSummaryAllergy = aiInsights.find(
        (item) => item.type === "LAB_SUMMARY_ALLERGY"
      );
      if (labSummaryAllergy?.value?.recommendations) {
        return labSummaryAllergy.value.recommendations;
      }
      // For discharge summary, return the discharge_advice_followup from value
      const dischargeSummary = aiInsights.find(
        (item) => item.type === "DISCHARGE_SUMMARY_INSIGHT"
      );
      if (dischargeSummary?.value?.discharge_advice_followup) {
        return dischargeSummary.value.discharge_advice_followup;
      }
      // Fallback to first item's type for other types
      return (
        aiInsights[0]?.type ||
        "Follow the recommendations based on the AI analysis"
      );
    }
    return "Check back later for AI-powered insights about this document.";
  };

  // Determine if this is a lab report based on insights data
  const getModalVariant = () => {
    if (aiInsights && aiInsights.length > 0) {
      const hasLabSummary = aiInsights.some(
        (item) =>
          item.type === "LAB_SUMMARY" ||
          item.type === "LAB_SUMMARY_PROCEDURE" ||
          item.type === "LAB_SUMMARY_IMAGING" ||
          item.type === "LAB_SUMMARY_ALLERGY"
      );
      if (hasLabSummary) return "labReport";
    }
    return "prescription";
  };

  return (
    <div
      className={[styles.card, selectMode && styles.selectMode, className]
        .filter(Boolean)
        .join(" ")}
      style={{
        cursor: selectMode ? "pointer" : undefined,
        border: viewer ? "none" : undefined,
      }}
    >
      <div className={styles.cardContent}>
        {/* Document Title — clickable in document listing variant; navigates to viewer. */}
        {(() => {
          const titleNode =
            viewer && typeof displayName === "string" ? (
              displayName
            ) : typeof displayName === "string" ? (
              <MWrap text={displayName} maxChars={30} />
            ) : (
              displayName
            );

          const titleIsClickable =
            !viewer &&
            !selectMode &&
            cardType === "document" &&
            !!onViewDocument;

          if (titleIsClickable) {
            return (
              <button
                type="button"
                className={styles.titleRow}
                onClick={handleViewDocumentClick}
              >
                <span className={styles.title}>{titleNode}</span>
                <IconChevronRight
                  size={16}
                  className={styles.titleChevron}
                  aria-hidden
                />
              </button>
            );
          }

          return (
            <div
              className={styles.title}
              style={
                viewer
                  ? {
                      color: "var(--brand-colours-indigo-indigo-700, #36116D)",
                      fontFamily:
                        "var(--typography-font-family-base, Manrope)",
                      fontSize: "var(--typography-font-size-h7, 24px)",
                      fontStyle: "normal",
                      fontWeight: 700,
                      lineHeight: "120%",
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                    }
                  : {}
              }
            >
              {titleNode}
            </div>
          );
        })()}

        {/* Category and Date Row */}
        <div className={styles.categoryDateRow}>
          {!viewer && (
            <div className={styles.categoryInfo}>
              <div className={styles.categoryIcon}>{icon}</div>
              <span className={styles.categoryName}>{categoryLabel}</span>
            </div>
          )}
          <div className={styles.dateInfo}>
            {meta && (
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span className={styles.dateText}>
                  {typeof meta === "string"
                    ? viewer
                      ? formatViewerDate(meta)
                      : `Dated ${meta}`
                    : meta}
                </span>
                {showStatusIcons &&
                  (_isImportant || _isPrivate || _availableOffline) && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {_isImportant && (
                        <IconStar size={14} color="#2A2A2A" title="Important" />
                      )}
                      {_availableOffline && (
                        <IconCloudDownload
                          size={14}
                          color="#2A2A2A"
                          title="Available Offline"
                        />
                      )}
                      {_isPrivate && (
                        <IconShieldLock
                          size={14}
                          color="#2A2A2A"
                          title="Private"
                        />
                      )}
                    </div>
                  )}
              </div>
            )}
          </div>
        </div>

        {/* AI Preview pill — shown only for document cards with insights enabled. */}
        {!hideActions && !selectMode && cardType === "document" && insights && (
          <div className={styles.actionsRow}>
            <button
              className={`${styles.aiPreviewCTA} ${
                insightsEnabled ? "" : styles.disabled
              }`}
              onClick={handleAIPreviewClick}
              type="button"
            >
              <div
                className={`${styles.aiIcon} ${
                  insightsEnabled ? "" : styles.disabled
                }`}
              >
                <IconSparkles size={12} color="#FFFFFF" />
              </div>
              <span
                className={`${styles.aiPreviewText} ${
                  insightsEnabled ? "" : styles.disabled
                }`}
              >
                AI preview
              </span>
              <IconChevronRight
                size={12}
                color={insightsEnabled ? "#B23F83" : "#AEAEAE"}
                aria-hidden
              />
              {/* Info icon — only when insights are off, indicating the disabled state. */}
              {!insightsEnabled && (
                <IconInfoCircle
                  size={12}
                  color="#3263A3"
                  style={{ marginLeft: "4px", cursor: "pointer" }}
                />
              )}
            </button>
          </div>
        )}
      </div>

      {/* Checkbox in Select Mode */}
      {selectMode && (
        <div className={styles.checkboxContainer}>
          <MCheckbox
            checked={selected}
            onChange={(e) => {
              e.stopPropagation();
              if (onSelect) onSelect();
            }}
          />
        </div>
      )}

      {/* Options Menu */}
      {!selectMode && cardType === "document" && !viewer && (
        <div className={styles.optionsMenu}>
          <IconDotsVertical
            className={`${styles.dotsIcon} document-card-options`}
            size={24}
            onClick={(e) => {
              if (e && e.stopPropagation) e.stopPropagation();
              if (onOptionsClick) onOptionsClick();
            }}
          />
        </div>
      )}
      {!selectMode && cardType !== "document" && optionsMenu && (
        <div className={styles.optionsMenu}>{optionsMenu}</div>
      )}

      {/* AI Modal */}
      {insights && (
        <MAIModal
          open={showAIModal}
          onClose={() => {
            console.log("Modal close called");
            setShowAIModal(false);
          }}
          content={getModalContent()}
          whatsNextTitle="What's next"
          whatsNextContent={getWhatsNextContent()}
          variant={getModalVariant()}
          insightsData={aiInsights}
          onInsightUpdated={(updated) => {
            setAiInsights((prev) =>
              prev.map((item) =>
                (item.id || item._id) === (updated.id || updated._id)
                  ? { ...item, ...updated }
                  : item
              )
            );
          }}
        />
      )}

      {/* Insights Disabled Dialog */}
      <MDialog
        isOpen={showDisabledDialog}
        onClose={() => setShowDisabledDialog(false)}
        title="Insights are turned off"
        description="Turn on AI Insights in Settings to view the insights for this document."
        primaryCtaText="Go to settings"
        secondaryCtaText="Cancel"
        onPrimaryCta={() => {
          navigate("/profile/notifications", {
            state: { backUrl: globalThis.location.pathname },
          });
        }}
        onSecondaryCta={() => setShowDisabledDialog(false)}
      />

      {/* No Insights Available Dialog */}
      <MDialog
        isOpen={showNoInsightsDialog}
        onClose={() => setShowNoInsightsDialog(false)}
        title="Insights not available."
        primaryCtaText="Done"
        onPrimaryCta={() => setShowNoInsightsDialog(false)}
      />
    </div>
  );
};

export default MDocumentCard;
